/**
 * Shared lifecycle stage data + slide-over panel.
 * Used by SaaSLandingPage (live) and HeroMockPage (A/B variants).
 *
 * Edit the stage copy / sample doctors in ONE place here — both home variants update.
 */
import React, { useEffect } from 'react';
import {
  Globe, Calendar, MapPin, Shield, Users, ArrowRight, Stethoscope, FileText,
  Search, TrendingUp, BarChart3, MessageCircle, Rocket, Share2, Newspaper,
  Sparkles, HeartPulse, BookOpen, Building2, X, Star, Microscope, Award,
  FileCheck, Library, Video, ScrollText, HandHeart, GraduationCap, Siren, Scale
} from 'lucide-react';

export const LIFECYCLE_QUADRANTS = [
  {
    icon: HeartPulse,
    stage: 'Stage 01',
    label: 'First Practice',
    headline: 'A permanent digital home, from day one.',
    question: 'Setting up your clinical practice for the first time?',
    needs: ['Patient Visibility', 'Start Getting Patients', 'Work under Seniors', 'Demonstrate Skills'],
    bg: 'from-sky-50 to-white',
    border: 'border-sky-200',
    accent: 'text-sky-700',
    accentSolid: 'bg-sky-600',
    badge: 'bg-sky-100 text-sky-700',
    bullet: 'bg-sky-500',
    sampleDoctor: { slug: 'dr-aarav-mehta', name: 'Dr. Aarav Mehta', specialty: 'ENT · KEM Alumni', tag: 'Demo' },
    features: [
      { icon: Globe, title: 'Personal Website', desc: 'Hosted, maintained, search-indexed — never expires.' },
      { icon: Calendar, title: 'Online Booking', desc: 'Patients book directly. No calls, no middlemen.' },
      { icon: MapPin, title: 'Multi-Location', desc: 'Show every clinic where you practice.' },
      { icon: Share2, title: 'WhatsApp + QR Sharing', desc: 'Spread your name on day one.' },
      { icon: Search, title: 'Auto-SEO Engine', desc: 'Google ranks you organically — no ads.' },
      { icon: Shield, title: 'NMC Verified Profile', desc: 'Trust markers that build credibility.' },
    ],
  },
  {
    icon: TrendingUp,
    stage: 'Stage 02',
    label: 'Established Practice',
    headline: 'Make your name the channel patients reach.',
    question: 'Already running an established clinical practice?',
    needs: ['Become a Stronger Brand', 'Maximize Patient Inflows', 'Establish High Cure Rates'],
    bg: 'from-emerald-50 to-white',
    border: 'border-emerald-200',
    accent: 'text-emerald-700',
    accentSolid: 'bg-emerald-600',
    badge: 'bg-emerald-100 text-emerald-700',
    bullet: 'bg-emerald-500',
    sampleDoctor: { slug: 'dr-rajesh-sharma', name: 'Dr. Rajesh Sharma', specialty: 'Cardiology · 15 yrs exp', tag: 'Live' },
    features: [
      { icon: Star, title: 'Patient Reviews & Ratings', desc: 'Surface high cure rates from real bookings.' },
      { icon: BookOpen, title: 'Case Studies (In Your Words)', desc: 'Authentic clinical voice, not templates.' },
      { icon: BarChart3, title: 'Lead Tracking Dashboard', desc: 'Source of every patient — Google, WhatsApp, QR.' },
      { icon: Share2, title: 'Patient Referral Engine', desc: 'Tracked codes — your patients become growth.' },
      { icon: Rocket, title: 'Visibility Accelerator', desc: '5 Links Challenge for Google credibility.' },
      { icon: MessageCircle, title: 'Direct Brand Channel', desc: 'WhatsApp + QR + SEO route to YOU, not aggregators.' },
    ],
  },
  {
    icon: BookOpen,
    stage: 'Stage 03',
    label: 'Mastery & Innovation',
    headline: 'Publish your mastery. Be cited by the field.',
    question: 'Have unique clinical innovations or insights to share with the field?',
    needs: ['Niche High-Complexity Cases', 'Publishing Platform', 'Institutional Visibility'],
    bg: 'from-amber-50 to-white',
    border: 'border-amber-200',
    accent: 'text-amber-700',
    accentSolid: 'bg-amber-600',
    badge: 'bg-amber-100 text-amber-700',
    bullet: 'bg-amber-500',
    sampleDoctor: { slug: 'dr-vikram-iyer', name: 'Dr. Vikram Iyer', specialty: 'Knee Surgery · 40+ Publications', tag: 'Demo' },
    features: [
      { icon: Newspaper, title: 'Medical Insights Hub', desc: 'Long-form articles, indexed in Google News.' },
      { icon: Microscope, title: 'Per-Procedure SEO Pages', desc: 'One landing page per niche procedure — owned by you.' },
      { icon: Award, title: 'Institutional Affiliations', desc: '"Trained at" + current institution prominently displayed.' },
      { icon: FileCheck, title: 'Schema.org Article Markup', desc: 'Structured citations that Google understands.' },
      { icon: Users, title: 'Peer Referral Surface', desc: 'Other doctors find you for complex tertiary cases.' },
      { icon: Sparkles, title: 'TurboCharge AI Publishing', desc: '2 AI pulls/month for content acceleration.' },
    ],
  },
  {
    icon: Building2,
    stage: 'Stage 04',
    label: 'Public Footprint',
    headline: 'A verified academic identity — without compromising conduct rules.',
    question: 'Senior consultant in a govt hospital where private practice isn\'t allowed?',
    needs: ['Global Academic Visibility', 'Global Acclaim', 'Peer Recognition & Lasting Legacy'],
    bg: 'from-violet-50 to-white',
    border: 'border-violet-200',
    accent: 'text-violet-700',
    accentSolid: 'bg-violet-700',
    badge: 'bg-violet-100 text-violet-700',
    bullet: 'bg-violet-500',
    sampleDoctor: { slug: 'dr-anjali-mehra', name: 'Dr. Anjali Mehra', specialty: 'CTVS · AIIMS Delhi · 28 yrs', tag: 'Demo' },
    features: [
      { icon: Building2, title: 'Verified Academic Identity', desc: 'Hospital up front — patients route to your OPD, not you.' },
      { icon: Library, title: 'Publication Archive', desc: 'Articles, achievements, journal record.' },
      { icon: Video, title: 'Lecture & CME Archive', desc: 'Recorded talks, slides, reading lists for fellows.' },
      { icon: Users, title: 'Peer Referral Hub', desc: 'Doctor-to-doctor channel for complex cases.' },
      { icon: Globe, title: 'Global Search Visibility', desc: 'Patients & peers find you on Google.' },
      { icon: ScrollText, title: 'Conduct-Rules Safe', desc: 'No booking widget, no fees, no solicitation.' },
    ],
  },
  {
    icon: HandHeart,
    stage: 'Stage 05',
    label: 'Practicing Emeritus',
    headline: 'Your skills didn\'t retire. We make sure the world still pays for them — on your terms.',
    question: 'Retired from hospital practice but still have decades of clinical wisdom to give?',
    needs: ['Continued Impact & Income', 'Mentorship & Masterclass', 'Civic Response', 'Memoir & Legacy'],
    bg: 'from-rose-50 to-white',
    border: 'border-rose-200',
    accent: 'text-rose-700',
    accentSolid: 'bg-rose-700',
    badge: 'bg-rose-100 text-rose-700',
    bullet: 'bg-rose-500',
    sampleDoctor: { slug: 'dr-saraswati-venkataraman', name: 'Dr. Saraswati Venkataraman', specialty: 'General Surgery · Emeritus · 42 yrs', tag: 'Demo' },
    features: [
      { icon: GraduationCap, title: '1:1 Mentorship Subscriptions', desc: 'Younger doctors subscribe monthly. You coach, they pay — recurring income, you set the rate.' },
      { icon: Library, title: 'Masterclass & Wisdom Library', desc: 'Record once, earn forever. Revenue-share every time a doctor learns from your archive.' },
      { icon: Siren, title: 'Civic-Duty Responder', desc: 'Opt in to government emergency calls. Free if you want it to be — or quote your price.' },
      { icon: BookOpen, title: 'Memoir & Serialised Publishing', desc: 'Write your story. Readers subscribe. Your family inherits the revenue stream.' },
      { icon: Scale, title: 'Expert Witness / Ethics Board', desc: 'Medico-legal reviews, ethics committee service — vetted, paid, on your calendar.' },
      { icon: Globe, title: 'Your Own Branded Website', desc: 'Not a directory entry. A full DocSite page that honors your lifetime — like every other doctor.' },
    ],
  },
];

export function LifecycleSlideOver({ stage, onClose }) {
  useEffect(() => {
    if (!stage) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = orig; window.removeEventListener('keydown', onKey); };
  }, [stage, onClose]);

  if (!stage) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid="lifecycle-slideover" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-2xl bg-white shadow-2xl overflow-y-auto" style={{ animation: 'lifecycleSlideIn 280ms ease-out' }}>
        <style>{`@keyframes lifecycleSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
        <div className={`bg-gradient-to-br ${stage.bg} border-b ${stage.border} px-6 md:px-10 py-7 relative`}>
          <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/80 hover:bg-white border border-slate-200 flex items-center justify-center" aria-label="Close" data-testid="lifecycle-slideover-close">
            <X size={18} className="text-slate-700" />
          </button>
          <span className={`text-[10px] uppercase tracking-[0.18em] font-bold ${stage.accent}`}>{stage.stage}</span>
          <h3 className="font-light text-slate-900 leading-tight mt-2" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.5rem, 2.4vw, 2rem)' }}>
            {stage.label} <span className="block font-medium mt-1">{stage.headline}</span>
          </h3>
          <p className="mt-3 text-sm text-slate-700 italic">{stage.question}</p>
        </div>

        <div className="px-6 md:px-10 py-8 space-y-9">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-3">Your needs at this stage</p>
            <ul className="space-y-2">
              {stage.needs.map(n => (
                <li key={n} className="flex items-start gap-3">
                  <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${stage.bullet}`} />
                  <span className={`text-base font-semibold ${stage.accent}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{n}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-4">What DocSite gives you</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {stage.features.map(f => {
                const Icon = f.icon;
                return (
                  <div key={f.title} className={`bg-gradient-to-br ${stage.bg} rounded-xl border ${stage.border} p-4 flex gap-3`}>
                    <div className={`w-9 h-9 rounded-lg ${stage.badge} flex items-center justify-center flex-shrink-0`}>
                      <Icon size={16} />
                    </div>
                    <div className="min-w-0">
                      <h5 className="font-semibold text-slate-900 text-sm leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.title}</h5>
                      <p className="text-xs text-slate-600 mt-0.5 leading-snug">{f.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {stage.sampleDoctor && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-3">See a doctor at your stage</p>
              <a href={`/dr/${stage.sampleDoctor.slug}`} target="_blank" rel="noreferrer" className="block bg-white border border-slate-200 rounded-2xl p-4 hover:border-slate-300 hover:shadow-md transition-all group" data-testid={`lifecycle-sample-${stage.sampleDoctor.slug}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-12 h-12 rounded-xl ${stage.badge} flex items-center justify-center flex-shrink-0`}>
                      <Stethoscope size={20} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {stage.sampleDoctor.name}
                        {stage.sampleDoctor.tag === 'Demo' && (
                          <span className="ml-2 text-[9px] uppercase tracking-[0.16em] font-bold bg-yellow-200 text-yellow-900 px-1.5 py-0.5 rounded align-middle">Demo</span>
                        )}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">{stage.sampleDoctor.specialty}</p>
                    </div>
                  </div>
                  <ArrowRight size={18} className="text-slate-400 group-hover:text-slate-700 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                </div>
                <p className="text-[11px] text-slate-500 mt-3 leading-snug">View their live DocSite practice surface in a new tab.</p>
              </a>
            </div>
          )}

          <div className="border-t border-slate-200 pt-6">
            <p className="text-sm text-slate-700 mb-3">Pick any plan that fits — Free, Premium, or TurboCharge. You can change it anytime.</p>
            <div className="flex flex-wrap gap-3">
              <a href={`/register?as=doctor&stage=${stage.stage.toLowerCase().replace(' ', '-')}&from=lifecycle_panel`} className={`rounded-full px-7 py-3 ${stage.accentSolid} text-white font-medium hover:shadow-lg flex items-center gap-2`} data-testid="lifecycle-slideover-cta">
                Get Started Free <ArrowRight size={16} />
              </a>
              <a href="/brochure" className="rounded-full px-7 py-3 bg-white text-slate-700 font-medium border border-slate-300 hover:bg-slate-50 flex items-center gap-2">
                <FileText size={15} /> Compare Plans
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
